import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Modal,
  Fade,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

import api from '../../../../utils/functions/api';
import { confirmAsync } from '../../../../utils/functions/globalDialog';
import * as actions from '../../../../redux/actions';
import RecurringExpenseFields from '../RecurringExpenseFields';
import { MODAL_STYLE, ACCENT_GRADIENT, ACCENT_COLOR, THAI_MONTHS, YEAR_OPTIONS, NAME_VARIABLES } from '../constants';
import { applyNameVars } from '../utils';

// "YYYY-MM" (รายเดือน) หรือ "YYYY" (รายปี) -> { year, month } (month มีความหมายแค่รายเดือน)
function parsePeriodKey(key, recurType) {
  if (!key) return null;
  if (recurType === 'monthly') {
    const [y, m] = key.split('-');
    return { year: Number(y), month: Number(m) - 1 };
  }
  return { year: Number(key), month: 0 };
}

function formatPeriodKey(year, month, recurType) {
  return recurType === 'monthly' ? `${year}-${String(month + 1).padStart(2, '0')}` : String(year);
}

const DAY_OPTIONS_31 = Array.from({ length: 31 }, (_, i) => i + 1);
// ปีไม่มีความหมาย (ถูกแทนที่ด้วยปี/เดือนปัจจุบันตอน backend สร้างรายการจริงเสมอ) ใช้ปีกลางๆ คงที่
// แค่เป็นตัวช่วยคำนวณ Date object ภายในเท่านั้น — 2000 เป็นปีอธิกสุรทิน (ก.พ. มี 29 วัน) ใช้หา
// จำนวนวันสูงสุดของแต่ละเดือนตอนรายปี กันเลือกวันที่ไม่มีจริง (เช่น 31 ก.พ.) ค้างอยู่แบบไม่รู้ตัว
const NEUTRAL_YEAR = 2000;
const daysInMonth = (m) => new Date(NEUTRAL_YEAR, m + 1, 0).getDate();

const toggleButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  '&.Mui-selected': {
    background: ACCENT_GRADIENT,
    color: '#fff',
    '&:hover': { background: ACCENT_GRADIENT, filter: 'brightness(0.95)' },
  },
};

// ฟอร์มสร้าง/แก้ไข template รายการเบิกประจำ — ถือ state ของฟอร์ม (react-hook-form + project/budget/
// customer select) และยิง POST/PUT เองทั้งหมด parent (ExpensesRecurringManage.js) แค่บอกว่าเปิด/ปิด
// modal ตอนไหน กับส่ง `editing` (row ที่จะแก้ไข หรือ null = สร้างใหม่) เข้ามา แล้วรอ onSaved callback
// ฟิลด์รายละเอียดค่าใช้จ่ายแยกเป็น ../RecurringExpenseFields.js ไม่ใช้ components/Forms/ExpensesApplyForm.js
// ของ module รายการเบิกจ่ายปกติ (ตามที่ขอ กันกระทบฟอร์มที่ใช้ร่วมกันทั่ว module เดิม + ปรับสไตล์อิสระ)
export default function RecurringFormModal({ open, editing, onClose, onSaved }) {
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.project);
  const budgetState = useSelector((state) => state.budget);
  const customer = useSelector((state) => state.customer);

  const [recurType, setRecurType] = useState('monthly');
  const [date, setDate] = useState(new Date());
  const [day, setDay] = useState(1);
  const [month, setMonth] = useState(0); // 0-11, มีความหมายเฉพาะตอนรายปี
  const [remindDays, setRemindDays] = useState(7);

  // ช่วงเวลาที่ให้เตือน (range_start/range_end) — ถ้าไม่ติ๊ก hasRange ไม่จำกัด (เตือนได้ตลอด)
  const now = new Date();
  const [hasRange, setHasRange] = useState(false);
  const [rangeStartYear, setRangeStartYear] = useState(now.getFullYear());
  const [rangeStartMonth, setRangeStartMonth] = useState(now.getMonth());
  const [rangeEndYear, setRangeEndYear] = useState(now.getFullYear());
  const [rangeEndMonth, setRangeEndMonth] = useState(now.getMonth());

  const [projectSelect, setProjectSelect] = useState(null);
  const [budgetSelect, setBudgetSelect] = useState(null);
  const [customerSelect, setCustomerSelect] = useState(null);

  const { control, handleSubmit, reset, setValue, watch } = useForm();

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
    dispatch(actions.customerAll({}));
  }, [dispatch]);

  useEffect(() => {
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [dispatch, projectSelect]);

  // รายปี: ถ้าเดือนที่เลือกมีวันน้อยกว่า day ที่เลือกไว้ (เช่น เลือกวัน 31 ไว้ก่อน แล้วเปลี่ยนเป็น ก.พ.)
  // ต้อง clamp day ลงมาก่อนเสมอ ไม่งั้น `new Date(2000, month, day)` จะ overflow ไปเดือนถัดไปเงียบๆ
  useEffect(() => {
    if (recurType !== 'yearly') return;
    const max = daysInMonth(month);
    if (day > max) setDay(max);
  }, [recurType, month, day]);

  // ประกอบ date object จาก day/month ที่เลือก — สร้างใหม่จาก NEUTRAL_YEAR ทุกครั้งแทนการ mutate date เดิม
  useEffect(() => {
    setDate(new Date(NEUTRAL_YEAR, month, day));
  }, [day, month]);

  // เติมค่าฟอร์มใหม่ทุกครั้งที่เปิด modal — สร้างใหม่ล้างฟอร์มเปล่า, แก้ไข prefill จาก `editing`
  useEffect(() => {
    if (!open) return;
    reset({});
    if (!editing) {
      setRecurType('monthly');
      setDay(1);
      setMonth(0);
      setRemindDays(7);
      setHasRange(false);
      setRangeStartYear(now.getFullYear());
      setRangeStartMonth(now.getMonth());
      setRangeEndYear(now.getFullYear());
      setRangeEndMonth(now.getMonth());
      setProjectSelect(null);
      setBudgetSelect(null);
      setCustomerSelect(null);
      return;
    }
    setRecurType(editing.recur_type);
    const editingDate = editing.date ? new Date(editing.date) : new Date();
    setDay(editingDate.getDate());
    setMonth(editingDate.getMonth());
    setRemindDays(editing.remind_days_before ?? 7);

    const parsedStart = parsePeriodKey(editing.range_start, editing.recur_type);
    const parsedEnd = parsePeriodKey(editing.range_end, editing.recur_type);
    setHasRange(!!(parsedStart || parsedEnd));
    setRangeStartYear(parsedStart?.year ?? now.getFullYear());
    setRangeStartMonth(parsedStart?.month ?? now.getMonth());
    setRangeEndYear(parsedEnd?.year ?? now.getFullYear());
    setRangeEndMonth(parsedEnd?.month ?? now.getMonth());

    setProjectSelect(editing.project || null);
    setBudgetSelect(editing.budget || null);
    setCustomerSelect(editing.customer || null);
    setValue('project', editing.project?._id);
    setValue('budget', editing.budget?._id);
    setValue('expenses_type', editing.expenses_type || 'REFUND');
    setValue('name', editing.name);
    setValue('customer', editing.customer?._id);
    setValue('payee.bank', editing.payee?.bank);
    setValue('payee.account_number', editing.payee?.account_number);
    setValue('payee.name', editing.payee?.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const rangeStartKey = hasRange ? formatPeriodKey(rangeStartYear, rangeStartMonth, recurType) : null;
  const rangeEndKey = hasRange ? formatPeriodKey(rangeEndYear, rangeEndMonth, recurType) : null;
  const rangeError = hasRange && rangeStartKey && rangeEndKey && rangeEndKey < rangeStartKey
    ? 'ช่วงสิ้นสุดต้องไม่มาก่อนช่วงเริ่มต้น'
    : null;

  const onSubmit = async (data) => {
    if (rangeError) return;
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (!confirm) return;
    const payload = {
      ...data,
      recur_type: recurType,
      date,
      remind_days_before: remindDays,
      range_start: rangeStartKey,
      range_end: rangeEndKey,
      budget: budgetSelect?._id || undefined,
      project: projectSelect?._id || projectSelect || undefined,
      customer: customerSelect?._id || undefined,
    };
    try {
      if (editing) {
        await api.put(`${process.env.REACT_APP_API_URL}/expenses-recurring/${editing._id}`, payload);
      } else {
        await api.post(`${process.env.REACT_APP_API_URL}/expenses-recurring`, payload);
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const dayChoices = recurType === 'yearly' ? Array.from({ length: daysInMonth(month) }, (_, i) => i + 1) : DAY_OPTIONS_31;
  // ระบบไม่สร้างรายการเบิกให้เอง — แค่เตือนก่อนวันที่รับเงิน remindDays วัน แล้วให้กดสร้างเองพร้อมกรอกยอด/
  // แนบหลักฐาน (ดู computePeriods ฝั่ง backend expensesRecurring.service.js)
  const previewText = recurType === 'monthly'
    ? `ทุกเดือน ระบบจะเตือนล่วงหน้า ${remindDays} วันก่อนวันที่ ${day} ให้กดสร้างรายการเบิกพร้อมแนบหลักฐาน`
    : `ทุกปี ระบบจะเตือนล่วงหน้า ${remindDays} วันก่อนวันที่ ${day} ${THAI_MONTHS[month]} ให้กดสร้างรายการเบิกพร้อมแนบหลักฐาน`;

  // ตัวอย่างชื่อรายการเมื่อแทนตัวแปรด้วยงวดปัจจุบัน ({ยอด} แทนตอนสร้างจริงหลังกรอกยอด)
  const sampleDue = new Date(now.getFullYear(), recurType === 'monthly' ? now.getMonth() : month, 1);
  const nameValue = watch('name');
  const nameHelper = (
    <Box component="span" sx={{ display: 'block' }}>
      ตัวแปร: {NAME_VARIABLES.map((v) => `${v.token} = ${v.example}`).join(' · ')}
      {nameValue && /\{(เดือน|ด|ปี|ปีเต็ม|ยอด)\}/.test(nameValue) && (
        <Box component="span" sx={{ display: 'block', mt: 0.5, color: ACCENT_COLOR }}>
          ตัวอย่าง: {applyNameVars(nameValue, sampleDue)}
        </Box>
      )}
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={MODAL_STYLE}>
          {/* header */}
          <Box
            sx={{
              background: ACCENT_GRADIENT,
              color: '#fff',
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EventRepeatRoundedIcon fontSize="small" />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {editing ? 'แก้ไขรายการเบิกประจำ' : 'เพิ่มรายการเบิกประจำ'}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* body */}
          <Box sx={{ p: 2.5, overflow: 'auto' }}>
            {/* ความถี่ + วันที่รับเงิน รวมแถวเดียวกัน ให้กระชับ ไม่ต้องแยกหัวข้อ/การ์ดใหญ่คนละส่วน */}
            <Grid container spacing={1.5} alignItems="flex-end">
              <Grid item xs={12} sm={5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>ความถี่</Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={recurType}
                  onChange={(_e, val) => val && setRecurType(val)}
                >
                  <ToggleButton value="monthly" sx={toggleButtonSx}>
                    <CalendarMonthRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
                    รายเดือน
                  </ToggleButton>
                  <ToggleButton value="yearly" sx={toggleButtonSx}>
                    <ReplayRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
                    รายปี
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={4} sm={recurType === 'yearly' ? 2 : 4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="recur-day-label">วันที่รับเงิน</InputLabel>
                  <Select labelId="recur-day-label" label="วันที่รับเงิน" value={day} onChange={(e) => setDay(Number(e.target.value))}>
                    {dayChoices.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {recurType === 'yearly' && (
                <Grid item xs={4} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="recur-month-label">เดือน</InputLabel>
                    <Select labelId="recur-month-label" label="เดือน" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                      {THAI_MONTHS.map((m, idx) => (
                        <MenuItem key={m} value={idx}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={4} sm={recurType === 'yearly' ? 2 : 3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="เตือนล่วงหน้า"
                  value={remindDays}
                  onChange={(e) => setRemindDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                  InputProps={{ endAdornment: <InputAdornment position="end">วัน</InputAdornment> }}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 1.25,
                mb: 2.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(124,108,240,0.1)' : 'rgba(124,108,240,0.06)'),
              }}
            >
              <EventAvailableRoundedIcon fontSize="small" sx={{ color: ACCENT_COLOR, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">{previewText}</Typography>
            </Box>

            <FormControlLabel
              sx={{ mb: hasRange ? 1 : 2 }}
              control={<Checkbox size="small" checked={hasRange} onChange={(e) => setHasRange(e.target.checked)} />}
              label={<Typography variant="body2">จำกัดช่วงเวลาที่ให้เตือน</Typography>}
            />
            {hasRange && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {recurType === 'monthly' && (
                    <FormControl size="small" sx={{ minWidth: 108 }}>
                      <InputLabel id="range-start-month-label">เดือนเริ่ม</InputLabel>
                      <Select labelId="range-start-month-label" label="เดือนเริ่ม" value={rangeStartMonth} onChange={(e) => setRangeStartMonth(Number(e.target.value))}>
                        {THAI_MONTHS.map((m, idx) => (
                          <MenuItem key={m} value={idx}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl size="small" sx={{ minWidth: 96 }}>
                    <InputLabel id="range-start-year-label">ปีเริ่ม</InputLabel>
                    <Select labelId="range-start-year-label" label="ปีเริ่ม" value={rangeStartYear} onChange={(e) => setRangeStartYear(Number(e.target.value))}>
                      {YEAR_OPTIONS.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <RemoveRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />

                  {recurType === 'monthly' && (
                    <FormControl size="small" sx={{ minWidth: 108 }}>
                      <InputLabel id="range-end-month-label">เดือนสิ้นสุด</InputLabel>
                      <Select labelId="range-end-month-label" label="เดือนสิ้นสุด" value={rangeEndMonth} onChange={(e) => setRangeEndMonth(Number(e.target.value))}>
                        {THAI_MONTHS.map((m, idx) => (
                          <MenuItem key={m} value={idx}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl size="small" sx={{ minWidth: 96 }} error={!!rangeError}>
                    <InputLabel id="range-end-year-label">ปีสิ้นสุด</InputLabel>
                    <Select labelId="range-end-year-label" label="ปีสิ้นสุด" value={rangeEndYear} onChange={(e) => setRangeEndYear(Number(e.target.value))}>
                      {YEAR_OPTIONS.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {rangeError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{rangeError}</Typography>
                )}
              </Box>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <RecurringExpenseFields
                expenses={editing}
                control={control}
                Controller={Controller}
                setValue={setValue}
                watch={watch}
                projects={projects}
                projectSelect={projectSelect}
                setProjectSelect={setProjectSelect}
                budgets={budgetState}
                budgetSelect={budgetSelect}
                setBudgetSelect={setBudgetSelect}
                customer={customer}
                customerSelect={customerSelect}
                setCustomerSelect={setCustomerSelect}
                showAmount={false}
                nameHelper={nameHelper}
              />

              <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button variant="outlined" onClick={onClose}>ยกเลิก</Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!!rangeError}
                  startIcon={<SaveRoundedIcon />}
                  sx={{ background: ACCENT_GRADIENT, '&:hover': { background: ACCENT_GRADIENT, filter: 'brightness(0.95)' } }}
                >
                  บันทึก
                </Button>
              </Stack>
            </form>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
